import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { isCurrent } from '../lib/navCurrent.js';

/**
 * The top bar's links on a phone or tablet - 29 September 2026.
 *
 * **What Craig saw:** every page reflowed on a phone and the navigation did not.
 * Nine operator links and eight agency ones wrapped into a block of text above
 * the page. Below 1024px they now sit behind one **Menu** button.
 *
 * **The panel carries everything the wide bar does, with no menu inside a
 * menu** (the one-row ruling of 29 September): the bar's links, then each
 * drop-down's destinations as a labelled group (Setup; the agency name), then
 * who you are and Sign out. Nothing is reachable only at one width.
 *
 * It closes on:
 *
 * - **a link press**, because the page is about to change under it;
 * - **Escape**;
 * - **a press anywhere outside it**, the button included;
 * - **the route changing** by any other means, as a backstop.
 *
 * **Focus goes back to the button** when it closes by a link, by Escape or by
 * the button itself, so a keyboard user is left where they started rather than
 * at the top of the document. A press outside leaves focus where that press put
 * it: moving it back would take it away from whatever they just clicked.
 */
export interface MenuLink {
  to: string;
  label: string;
  end?: boolean;
}

/** A labelled run of links in the panel: what a drop-down holds on the wide bar. */
export interface MenuGroup {
  label: string;
  links: readonly MenuLink[];
}

export function MobileMenu({
  links,
  groups = [],
  children,
}: {
  links: readonly MenuLink[];
  groups?: readonly MenuGroup[];
  /** Who you are, the payments sentence and Sign out, below everything else. */
  children: ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const location = useLocation();

  function close(returnFocus: boolean): void {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }

  // The backstop: a route change from anywhere closes it.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    }

    function onPointerDown(event: Event): void {
      const target = event.target as Node | null;
      if (!target) return;
      // The button toggles itself; leave it to its own click handler.
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    // Into the panel, on its first link, so the keyboard can carry on from there.
    panelRef.current?.querySelector<HTMLElement>('a[href]')?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          if (open) close(true);
          else setOpen(true);
        }}
        className="ml-auto inline-flex items-center gap-2 rounded-control px-3 py-2 text-sm font-semibold text-ink ring-1 ring-line-strong ring-inset hover:bg-raise focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue"
      >
        <span aria-hidden="true" className="flex w-4 flex-col gap-[3px]">
          <span className="block h-0.5 w-4 bg-ink" />
          <span className="block h-0.5 w-4 bg-ink" />
          <span className="block h-0.5 w-4 bg-ink" />
        </span>
        Menu
      </button>

      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          data-testid="menu-panel"
          className="absolute inset-x-0 top-full z-40 max-h-[calc(100dvh-4rem)] overflow-y-auto border-b border-line bg-ground-2 px-4 pt-2 pb-5 shadow-lg sm:px-6"
        >
          <nav aria-label="Main">
            <PanelLinks links={links} onChoose={() => close(true)} />
            {groups.map((group) => (
              <div key={group.label} className="mt-4">
                <p className="truncate font-mono text-xs tracking-wide text-muted uppercase">
                  {group.label}
                </p>
                <PanelLinks links={group.links} onChoose={() => close(true)} />
              </div>
            ))}
          </nav>
          <div className="mt-4 flex flex-wrap items-center gap-3">{children}</div>
        </div>
      ) : null}
    </>
  );
}

function PanelLinks({
  links,
  onChoose,
}: {
  links: readonly MenuLink[];
  onChoose: () => void;
}): React.JSX.Element {
  const location = useLocation();
  return (
    <ul className="flex flex-col">
      {links.map((item) => {
        const current = isCurrent(item, location);
        return (
          <li key={item.to}>
            <Link
              to={item.to}
              aria-current={current ? 'page' : undefined}
              onClick={onChoose}
              className={`block border-b border-line py-3 text-base ${
                current ? 'font-semibold text-blue' : 'text-ink-2 hover:text-ink'
              } focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue`}
            >
              {item.label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
